import { Ref, RefObject, useState, useRef, useEffect } from "react"
import styled from "styled-components"
import SignaturePad from 'react-signature-canvas'

import type {
    TSignType,
} from "../types"

import { development } from "../constants"

import {
    getColorPickerArray,
    getFontPickerArray,
} from '../helpers'

import '../../assets/fonts-sign/fonts-sign.css'

import textIcon from '../../assets/sign-text-icon.svg'
import textIconHover from '../../assets/sign-text-hover-icon.svg'
import drawIcon from '../../assets/sign-draw-icon.svg'
import drawIconHover from '../../assets/sign-draw-hover-icon.svg'
import uploadIcon from '../../assets/sign-upload-icon.svg'
import uploadIconHover from '../../assets/sign-upload-hover-icon.svg'
import clearIcon from '../../assets/rearrange-delete-icon.svg'


const Wrapper = styled.div`
    width: 100%;
    padding: 28px 10px 12px;

    display: flex;
    flex-direction: column;
    align-items: center;

    background: rgba(255, 255, 255);
    /**box-shadow: 0px 12px 20px rgba(0, 108, 130, 0.4);*/
    box-shadow: 0px 4px 12px #006c8281;
    border-radius: 12px;
    box-sizing: border-box;

    @media screen and (max-width: 960px) {
        padding: 16px 10px 12px;
    }

    @media screen and (max-width: 640px) {
        border-radius: 0;
    }

     @media screen and (max-width: 480px) {
        padding: 12px 10px 8px;
        border-radius: 0;
    }
`


const TypeButtons = styled.div`
    margin-bottom: 28px;
    
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    background: #F6F6F6;
    border-radius: 8px;

    @media screen and (max-width: 960px) {
        margin-bottom: 16px;
    }

    @media screen and (max-width: 480px) {
        margin-bottom: 12px;
    }
`

const TypeButton = styled.button<{
    active: boolean,
}>`
    width: 115px;
    height: 52px;
    padding: 12px 0 12px 50px;

    background-repeat: no-repeat;
    background-position: 18px center;
    background-color: ${props => props.active ? '#D5F0F6' : 'transparent'};
    border: none;
    border-radius: 8px;
    outline: none;

    text-align: left;
    color: ${props => props.active ? '#2BA8C1' : '#939C9F'};
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: 21px;

    z-index: 1;
    cursor: pointer;

    @media screen and (max-width: 480px) {
        width: 100px;
        height: 36px;
        padding: 0 0 0 46px;
        background-position: 14px center;
        font-size: 16px;
        line-height: 18px;
    }
`

const TypeTextButton = styled(TypeButton)`
    background-image: url(${props => props.active ? textIconHover : textIcon});
`

const TypeDrawButton = styled(TypeButton)`
    background-image: url(${props => props.active ? drawIconHover : drawIcon});
`

const TypeUploadButton = styled(TypeButton)`
    padding: 0 0 0 42px;
    background-position: 12px center;
    background-image: url(${props => props.active ? uploadIconHover : uploadIcon});

    @media screen and (max-width: 480px) {
        padding: 0 0 0 36px;
        background-position: 8px center;
    }
`

const ColorWrapper = styled.div`
    margin: 12px 0 0 0;
    display: flex;
    flex-direction: row;

    @media screen and (max-width: 480px) {
        margin: 8px 4px 0 0;
    }
`

const UseColor = styled.div<{
    color: string,
    border: string,
}>`
    width: 32px;
    height: 32px;
    margin: 0 4px 0 0;

    background: ${props => props.color};
    border-radius: 50%;
    border: 4px solid ${props => props.border};
    
    transition: border .2s; 
    cursor: pointer;
`

const ColorPicker = ({
    colorIndex,
    setColorIndex,
    signaturePadRef,
    setSignImg,
}: {
    colorIndex: number,
    setColorIndex: (colorIndex: number) => void,
    signaturePadRef: RefObject<SignaturePad>,
    setSignImg: (signImg: string | undefined) => void,
}) => {
    const colors: {color: string, border: string}[] = getColorPickerArray()

    return <ColorWrapper>
        {
            colors.map((c, i) => <UseColor
                key={`color-pick-${i}`}
                color={c.color}
                border={i === colorIndex ? c.border : '#fff'}
                onClick={() => {
                    if (signaturePadRef?.current) {
                        const current = signaturePadRef.current
                        const data = current.getSignaturePad()?.toData()
                        if (data) {
                            data.forEach(path => path.forEach(point => point.color = c.color))
                            current.clear()
                            current.fromData(data)
                            const svg = current.toDataURL('image/svg+xml')
                            if (svg) setSignImg(svg)
                        }
                    }
                    return setColorIndex(i)
                }}
            />)
        }
    </ColorWrapper>
}

const NameWrapper = styled.div`
    width: 560px;
    margin-bottom: 16px;

    display: flex;
    flex-direction: column;
    align-items: center;

    @media screen and (max-width: 640px) {
        width: calc(100% - 28px);
    }

    @media screen and (max-width: 480px) {
        width: 100%;
        margin-bottom: 12px;
    }
`

const NameInput = styled.input`
    width: 100%;
    height: 56px;
    padding-left: 16px;

    background: #FFFFFF;
    border: 2px solid #E6E6E6;
    box-sizing: border-box;
    border-radius: 8px;
    box-shadow: 0px 0px 0px 4px transparent;

    color: #404748;
    font-style: normal;
    font-weight: 400;
    font-size: 18px;
    line-height: 24px;

    outline: none;
    transition: box-shadow .3s, border .2s;

    &::placeholder {
        color: #B3B3B3;
    }

    &:focus {
        border: 2px solid #8DD8E7;
        box-shadow: 0px 0px 0px 4px #D5F1F6;
    }

    &:hover {
        border: 2px solid #8DD8E7;
    }

    @media screen and (max-width: 480px) {
        height: 44px;
        font-size: 16px;
    }
`

const Name = ({
    userName,
    setUserName,
}: {
    userName: string,
    setUserName: (userName: string) => void,
}) => {
    return <NameWrapper>
        <NameInput
            type='text'
            placeholder='Enter Your Name'
            value={userName}
            onChange={e => setUserName(e.target.value)}
        />
    </NameWrapper>
}

const ContentWrapper = styled.div`
    width: 560px;
    height: 264px;
    padding: 0;
    position: relative;

    background: #FCFCFC;
    border: 2px solid #E6E6E6;
    box-sizing: border-box;
    border-radius: 8px;

    overflow: hidden;

    @media screen and (max-width: 640px) {
        width: calc(100vw - 24px * 2);
    }

    @media screen and (max-width: 480px) {
        width: calc(100vw - 10px * 2);
        height: calc((100vw - 10px * 2) * 200 / 300);
    }
`

const ClearButton = styled.div`
    width: 24px;
    height: 24px;

    position: absolute;
    top: 8px;
    right: 8px;

    background-color: #FC531B;
    background-repeat: no-repeat;
    background-position: center;
    background-size: 14px;
    background-image: url(${clearIcon});
    border-radius: 8px;

    cursor: pointer;
    z-index: 1;
`

const FontWrapper = styled.div`
    width: 560px;
    height: 264px;
    padding: 12px 8px 12px 12px;
    position: relative;

    background: #FCFCFC;
    border: 2px solid #E6E6E6;
    box-sizing: border-box;
    border-radius: 8px;

    overflow: hidden;

    @media screen and (max-width: 640px) {
        width: calc(100% - 28px);
    }

    @media screen and (max-width: 480px) {
        width: 100%;
        height: 200px;
        padding: 8px 4px 8px 2px;
    }
`

const ContentScroller = styled.div`
    width: 100%;
    height: 100%;

    overflow-y: auto;
    overflow-x: hidden;

    // Moz scroll
    @-moz-document url-prefix() {
        & {
            scrollbar-width: auto;
            scrollbar-color: #C4C4C4A0 transparent;
        }
    }

    &::-webkit-scrollbar {
        width: 8px;
        background: #F2F2F2;
        border-radius: 12px;
    }

    &::-webkit-scrollbar-thumb {
        width: 8px;
        height: 50px;

        border-radius: 12px;
        background: #C4C4C4A0;
    }

    @media screen and (max-width: 480px) {
         &::-webkit-scrollbar {
            width: 4px;
            border-radius: 6px;
        }

        &::-webkit-scrollbar-thumb {
            width: 4px;

            border-radius: 6px;
        }
    }
`

const SignTextRadio = styled.div<{
    selected: boolean,
}>`
    width: 20px;
    height: 20px;
    margin: 0 6px 0 16px;

    background: #fff;
    border-radius: 50%;
    border: ${props => props.selected ? '6px solid #2BA8C1' : '4px solid #CCCCCC'};

    transition: border .2s;

    @media screen and (max-width: 480px) {
        margin: 0 0 0 10px;
    }
`

const SignTextName = styled.div`
    width: calc(100% - 56px);
    padding: 0 0 0 10px;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    font-style: normal;
    font-weight: 400;
    font-size: 32px;
`

const SignTextElement = styled.div<{
    font: string,
    color: string,
    selected: boolean,
}>`
    width: calc(100% - 8px);
    height: 60px;

    display: flex;
    flex-direction: row;
    align-items: center;

    background: ${props => props.selected ? '#E8F7FA' : 'transparent'};
    border-radius: 8px;

    font-family: ${props => props.font};
    color: ${props => props.color};
    
    transition: background .2s;
    user-select: none;
    cursor: pointer;

    &:hover {
        background: #E8F7FA;
    }

    @media screen and (max-width: 480px) {
        width: calc(100% - 2px);
        border-radius: 0;
    }
`

const FontPicker = ({
    fontIndex,
    setFontIndex,
    userName,
    colorIndex,
}: {
    fontIndex: number,
    setFontIndex: (colorIndex: number) => void,
    userName: string,
    colorIndex: number,
}) => {
    const fonts: string[] = getFontPickerArray()
    const color = getColorPickerArray()[colorIndex].color

    return <FontWrapper>
        <ContentScroller>
        {
            fonts.map((f, i) => <SignTextElement
                key={`font-pick-${i}`}
                font={f}
                color={color}
                selected={i === fontIndex}
                onClick={() => setFontIndex(i)}
            >
                <SignTextRadio selected={i === fontIndex} />
                <SignTextName>{userName.length ? userName : 'Example'}</SignTextName>
            </SignTextElement>)
        }
        </ContentScroller>
    </FontWrapper>
}

const DrawWrapper = styled(ContentWrapper)`
    @media screen and (max-width: 480px) {
        height: calc((100vw - 10px * 2) * 172 / 300);
    }

    & canvas {
        position: absolute;
        top: 0px;
        left: 0px;
    }
`

const SignatureDrawMassage = styled.div<{
    show: boolean,
}>`
    @keyframes show{
        0%{
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }
    width: 100%;
    height: 100%;

    position: absolute;
    top: 0;
    left: 0;

    display: ${props => props.show ? 'flex' : 'none'};
    justify-content: center;
    align-items: center;

    user-select: none;
    opacity: 0;
    animation: show 1s 1;
    animation-fill-mode: forwards;
`

const SignatureDrawMassageText = styled.div`
    width: 360px;

    color: #939C9F;
    font-weight: 400;
    font-size: 20px;
    line-height: 28px;
    text-align: center;
`

const getCanvasSize = () => {
    const clientWidth = document.documentElement.clientWidth
    let width, height
    if (clientWidth <= 480) {
        width = clientWidth - 10 * 2
        height = width * 172 / 300
    }
    else if (clientWidth <= 640) {
        width = clientWidth - 24 * 2
        height = 264
    }
    else {
        width = 560
        height = 264
    }
    return {
        width: Math.ceil(width * Math.max(window.devicePixelRatio || 1, 1)),
        height: Math.ceil(height * Math.max(window.devicePixelRatio || 1, 1)),
    }
}

const DrawSign = ({
    colorIndex,
    signaturePadRef,
    setSignImg,
    showDrawMassage,
    setShowDrawMassage,
}: {
    colorIndex: number,
    signaturePadRef: RefObject<SignaturePad>,
    setSignImg: (signImg: string | undefined) => void,
    showDrawMassage: boolean,
    setShowDrawMassage: (set: boolean) => void,
}) => {
    const color = getColorPickerArray()[colorIndex].color
    const canvasSize = getCanvasSize()

    return <>
        <DrawWrapper
            onMouseDown={() => setShowDrawMassage(false)}
            onTouchStart={() => setShowDrawMassage(false)}
        >
            <SignatureDrawMassage show={showDrawMassage}>
                <SignatureDrawMassageText>Please draw your signature using a mouse or touchpad</SignatureDrawMassageText>
            </SignatureDrawMassage>
            <SignaturePad
                ref={signaturePadRef}
                penColor={color}
                minWidth={1}
                maxWidth={3}
                canvasProps={{
                    width: canvasSize.width,
                    height: canvasSize.height,
                }}
                onEnd={(e) => {
                    const svg = signaturePadRef.current?.toDataURL('image/svg+xml')
                    if (svg) setSignImg(svg)                    
                }}
            />
            <ClearButton
                title={`Clear`}
                onClick={() => {
                    signaturePadRef.current?.clear()
                    setShowDrawMassage(true)
                    setSignImg(undefined)
                }}
            />
        </DrawWrapper>
    </>
}

const UploadScreen = styled.div<{
    dragOver: boolean,
}>`
    position: ${development ? 'absolute' : 'fixed'};
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;

    z-index: ${props => props.dragOver ? '10' : '0'};
`

const UploadWrapper = styled.div<{
    dragOver: boolean,
}>`
    width: 100%;
    height: 100%;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    background: ${props => props.dragOver ? '#E8F7FA80' : 'transparent'};
`

const UploadButton = styled.button`
    width: 240px;
    height: 60px;

    background: #D5F0F6;
    border-radius: 8px;
    border: none;

    color: #2BA8C1;
    font-style: normal;
    font-weight: 700;
    font-size: 20px;
    line-height: 24px;

    z-index: 1;
    cursor: pointer;
    user-select: none;
    transition: background .2s;

    &:hover {
        background: #C0E9F1;
    }
`

const UploadDescription = styled.span`
    margin-top: 6px;

    color: #939C9F;
    text-align: center;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
`

const UploadSignWrapper = styled(ContentWrapper)`
    margin-bottom: 16px;

    @media screen and (max-width: 480px) {
        margin-bottom: 8px;
    }
`

const UploadSign = ({
    signImg,
    setSignImg,
}: {
    signImg: string | undefined,
    setSignImg: (signImg: string | undefined) => void,
}) => {
    const [dragOver, setDragOver] = useState(false)
    const refInput = useRef<HTMLInputElement>(null)

    const getImgSrc = (file: File) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            const src = e?.target?.result
            if (typeof src === 'string') setSignImg(src)
        }
        reader.readAsDataURL(file)
    }

    return <UploadSignWrapper>
        <UploadScreen
            onDrop={(e) => {
                e.preventDefault()
                setDragOver(false)
                const files = e.dataTransfer?.files
                if (files && files[0]) {
                    if (files[0].type.indexOf('image') !== -1) return getImgSrc(files[0])
                    return setSignImg(undefined)
                }
            }}
            onDragOver={(e) => {
                e.preventDefault()
                setDragOver(true)
            }}
            onDragLeave={() => {
                setDragOver(false)
            }}
            dragOver={dragOver}
        />
        {
            !signImg &&
            <UploadWrapper
                dragOver={dragOver}
            >
                <UploadButton
                    onClick={() => {
                        if (refInput.current) refInput.current.click()
                    }}
                >Upload Signature</UploadButton>
                <UploadDescription>or drop file here</UploadDescription>
            </UploadWrapper>
        }
        {
            !!signImg &&
            <UploadWrapper
                dragOver={dragOver}
            >
                <img
                    style={{
                        position: 'absolute',
                        maxWidth: '100%',
                        maxHeight: '100%',
                    }}
                    src={signImg}
                />
            </UploadWrapper>
        }
        {
            !!signImg &&
            <ClearButton
                title={`Clear`}
                onClick={() => {
                    setSignImg(undefined)
                }}
            />
        }
        <input
            type="file"
            accept="image/*"
            ref={refInput}
            style={{ display: 'none' }}
            multiple={false}
            onChange={(e) => {
                const files = e.target.files
                if (files && files[0]) {
                    if (files[0].type.indexOf('image') !== -1) getImgSrc(files[0])
                    else setSignImg(undefined)
                }
                e.target.value = ''
            }}
        />
    </UploadSignWrapper>
}

export const SignH1 = styled.h1`
    color: #fff;
    font-style: normal;
    font-weight: 700;
    font-size: 24px;
    line-height: 28px;
    margin: 8px 0px 12px;

    @media screen and (max-width: 960px) {
        font-size: 28px;
        line-height: 1.3;
    }

    @media screen and (max-width: 480px) {
        font-size: 28px;
        line-height: 1.3;
    }
`

const Sign = ({
    type,
    setType,
    colorIndex,
    setColorIndex,
    userName,
    setUserName,
    fontIndex,
    setFontIndex,
    signImg,
    setSignImg,
}: {
    type: TSignType,
    setType: (type: TSignType) => void,
    colorIndex: number,
    setColorIndex: (colorIndex: number) => void,
    userName: string,
    setUserName: (userName: string) => void,
    fontIndex: number,
    setFontIndex: (colorIndex: number) => void,
    signImg: string|undefined,
    setSignImg: (signImg: string | undefined) => void,
}) => {
    const signaturePadRef = useRef<SignaturePad>(null)
    const resetHandle = () => {
        setSignImg(undefined)
        setUserName('')
    }

    const [showDrawMassage, setShowDrawMassage] = useState(true)

    const [reRender, setReRender] = useState(false)
    const resize: () => void = () => setReRender(!reRender)
    useEffect(() => window.onresize = resize)

    return <Wrapper>
        <TypeButtons>
            <TypeTextButton
                active={type === 'text'}
                onClick={() => {
                    resetHandle()
                    setShowDrawMassage(true)
                    return setType('text')
                }}
            >Text</TypeTextButton>
            <TypeDrawButton
                active={type === 'draw'}
                onClick={() => {
                    resetHandle()
                    return setType('draw')
                }}
            >Draw</TypeDrawButton>
            <TypeUploadButton
                active={type === 'upload'}
                onClick={() => {
                    resetHandle()
                    setShowDrawMassage(true)
                    return setType('upload')
                }}
            >Upload</TypeUploadButton>
        </TypeButtons>
        {
            type === 'text' &&
            <Name
                userName={userName}
                setUserName={setUserName}
            />
        }
        {
            type === 'text' &&
            <FontPicker
                fontIndex={fontIndex}
                setFontIndex={setFontIndex}
                userName={userName}
                colorIndex={colorIndex}
            />
        }
        {
            type === 'draw' &&
            <DrawSign
                colorIndex={colorIndex}
                signaturePadRef={signaturePadRef}
                setSignImg={setSignImg}
                showDrawMassage={showDrawMassage}
                setShowDrawMassage={setShowDrawMassage}
            />
        }
        {
            (type === 'text' || type === 'draw') &&
            <ColorPicker
                colorIndex={colorIndex}
                setColorIndex={setColorIndex}
                signaturePadRef={signaturePadRef}
                setSignImg={setSignImg}
            ></ColorPicker>
        }
        {
            type === 'upload' &&
            <UploadSign
                signImg={signImg}
                setSignImg={ setSignImg}
            />
        }
    </Wrapper>
}

export default Sign